import { type FrameStart, getFrameStart, isFrameStart } from '../metadata/frameStart';
import { getEventMetadata } from '../metadata/eventMetadata';
import { type Event, isEvent } from './event';
import type { AnyEventBase } from '@snapchat/blizzard-schema';
import { getLogger } from '../logger';
import { logDebugAction } from '../debug/debug';
import { jsonLogQueue, type JsonLogQueue } from './queue/jsonLogQueue';

const log = getLogger('logEvent');

type LogEvent<AllowedEvent extends AnyEventBase> = (e: AllowedEvent) => void;
type BufferedItem = Event<AnyEventBase> | Promise<FrameStart | null>;

// TODO(kho): This function returns a function, which is a bit weird. It should be refactored to be a
// constant with a cast. I.e. logEventTyped = logEvent as LogEvent<AnyEventBase>
export const logTypedEvent = <AllowedEvent extends AnyEventBase>(): LogEvent<AllowedEvent> =>
  logEvent as LogEvent<AllowedEvent>;

let pendingItems = 0;

/**
 * Logs an event.
 *
 * If Blizzard is not completely initialized (including receiving user information), this will
 * buffer the event internally until it is.
 *
 * @param event An event from the Blizzard event schema
 */
export async function logEvent<AllowedEvent extends AnyEventBase>(
  event: AllowedEvent
): Promise<void> {
  if (!initCalled) {
    throw new Error('initBlizzard must be called prior to calling logEvent');
  }

  const items: BufferedItem[] = [];

  // Capture client timestamp (must be first operation to capture event time correctly)
  const clientTs = new Date();

  log.info('Logging event', event.event_name, event);

  // Retrieve a FrameStart. This contains metadata shared across all events.
  const frameStart = getFrameStart();
  items.push(frameStart);

  // Retrieve event-level metadata (changes frequently or is per-event, so not appropriate for the FrameStart).
  const metadata = getEventMetadata(event.event_name, clientTs);

  // Wrap and add to buffer
  const wrappedEvent: Event<AllowedEvent> = {
    eventName: event.event_name,
    metadata,
    data: event,
  };
  items.push(wrappedEvent);
  logDebugAction({ type: 'EventLogged', event: wrappedEvent });

  if (pendingItems > items.length) {
    log.debug(`#logEvent: Pending items: ${pendingItems}`);
  }

  // Attempt to drain buffer to the log queue if we're ready
  await enqueueItems(items);
}

/**
 * Enqueue the given items. Waits for Blizzard to be ready to transmit the items, and for any
 * asynchronous data in the Frame Starts (e.g. user GUID) to have resolved.
 */
async function enqueueItems(items: BufferedItem[]): Promise<void> {
  // Resolve any promised FrameStarts
  // Apart from initial startup, these should instantly resolve
  const resolvedItems = await Promise.all(items);

  for (const item of resolvedItems) {
    pendingItems--;

    if (item === null) {
      continue;
    } // Skip any FrameStarts which resolved to null

    if (isFrameStart(item)) {
      log.info('Enqueuing a new FrameStart', item);
      logDebugAction({ type: 'FrameStartEnqueued', frameStart: item });
    } else if (isEvent(item)) {
      log.debug('Enqueuing an event', item);
      logDebugAction({ type: 'EventEnqueued', event: item });
    }

    await getLogQueue().append(item);

    log.debug(`#enqueueItems: ${pendingItems} remaining`);
  }
}

/** Get the log queue. */
function getLogQueue(): JsonLogQueue {
  // isFramesEnabled is memoized so won't change during execution, so this is safe.
  return jsonLogQueue;
}

/** @internal */
export let initCalled = false;

/**
 * Set the initCalled flag to true.
 *
 * @internal
 */
export function setInitCalled(): void {
  initCalled = true;
}
